import React, { useState, useEffect, useRef } from "react";
import { apiClient, getServiceUrl } from "../../utils/apiClient";
import {
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
  Paper,
  SxProps,
  Theme,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RefreshIcon from "@mui/icons-material/Refresh";
import { EmailMessageComponent } from "./EmailMessageComponent";
import JSONString from "../components/JSONString";
import { TinyValueComponent } from "../components/TinyValueComponent";
import { DownloadFileButton } from "../components/DownloadButton";
import {
  EmailGetThreadResponse,
  EmailMessage,
} from "../../../../src/documents/email/types";
import { useSelector } from "react-redux";
import { RootState } from "../../store/rootReducer";
import { AdminEmailPanel } from "./AdminEmailPanel";

interface Props {
  threadId: string;
  selectedMessageId?: string;
  showMessageMetadata?: boolean;
  additionalMetadataObject?: any;
  sx?: SxProps<Theme>;
  loadOnlySelectedMessage?: boolean;
  showMessageIds?: boolean;
  inboxEmail?: string; // Optional inbox email for admin override
  sharedInbox?: string;
}

export const InternalEmailThreadComponent: React.FC<Props> = ({
  threadId,
  selectedMessageId,
  showMessageMetadata,
  additionalMetadataObject,
  sx,
  loadOnlySelectedMessage,
  showMessageIds,
  inboxEmail,
  sharedInbox,
}) => {
  const [thread, setThread] = useState<EmailGetThreadResponse | null>(null);
  const [initialThreadLoading, setInitialThreadLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedForwardedEmail, setSelectedForwardedEmail] =
    useState<EmailMessage | null>(null);
  const [currentLoadingIndex, setCurrentLoadingIndex] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Get user data from Redux store to check admin status
  const user = useSelector((state: RootState) => state.userData.user);

  useEffect(() => {
    const fetchThread = async () => {
      try {
        setThread(null);
        setError(null);
        setInitialThreadLoading(true);
        const url = `/documents/email/thread/${threadId}?include_messages_body=false&inboxEmail=${encodeURIComponent(inboxEmail || "")}&sharedInbox=${encodeURIComponent(sharedInbox || "")}`;
        const response = await apiClient.get<EmailGetThreadResponse>(url, {
          withCredentials: true,
        });

        const data = response;
        if (data.messages && data.messages.length > 0) {
          data.messages.sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
          );
        }
        setThread(data);

        // If selectedMessageId is provided, jump to that message
        if (selectedMessageId && data.messages && data.messages.length > 0) {
          const index = data.messages.findIndex(
            (msg) => msg.messageId === selectedMessageId
          );
          setCurrentIndex(index !== -1 ? index : 0);
        } else {
          setCurrentIndex(0);
        }

        setCurrentLoadingIndex(0);
      } catch (err) {
        setError(String(err));
      } finally {
        setInitialThreadLoading(false);
      }
    };

    fetchThread();
  }, [threadId, selectedMessageId, inboxEmail]);

  useEffect(() => {
    if (!thread) return;
    if (!thread.messages || thread.messages.length === 0) return;
    if (
      !loadOnlySelectedMessage &&
      currentLoadingIndex >= thread.messages.length
    ) {
      return;
    }

    const fetchMessageDetails = async (messageIndex: number) => {
      try {
        setThread((prev) => {
          if (!prev) return null;
          if (prev.messages[messageIndex]) {
            (prev.messages[messageIndex] as any)._loading = true;
          }
          return prev;
        });
        const message = thread.messages[messageIndex];
        const url = `/documents/email/thread/${message.threadId}/message/${message.messageId}?extracted_data=1&inboxEmail=${encodeURIComponent(inboxEmail || "")}&sharedInbox=${encodeURIComponent(sharedInbox || "")}`;
        const response = await apiClient.get<EmailMessage>(url, {
          withCredentials: true,
        });
        const details = response;

        setThread((prev) => {
          if (!prev) return null;
          const updatedMessages = [...prev.messages];
          updatedMessages[messageIndex] = {
            ...updatedMessages[messageIndex],
            body: details.body,
            htmlBody: details.htmlBody,
            headerMessageId: details.headerMessageId,
            attachments: details.attachments,
            mailAttachments: details.mailAttachments,
            tags: details.tags,
            metadata: details.metadata,
            extractedData: details.extractedData,
          };
          return { ...prev, messages: updatedMessages };
        });
      } catch (err) {
        setThread((prev) => {
          if (!prev) return null;
          const updatedMessages = [...prev.messages];
          updatedMessages[messageIndex] = {
            ...updatedMessages[messageIndex],
            body: "Error Fetching Email 🥺 \n",
            attachments: [],
            mailAttachments: [],
          };
          (updatedMessages[messageIndex] as any)._error = true;
          (updatedMessages[messageIndex] as any)._loading = false;
          return { ...prev, messages: updatedMessages };
        });
        console.error(err);
      }
    };

    if (
      loadOnlySelectedMessage &&
      currentIndex < thread.messages.length &&
      !(thread.messages[currentIndex] as any)._loading
    ) {
      fetchMessageDetails(currentIndex);
    } else if (!loadOnlySelectedMessage) {
      fetchMessageDetails(currentLoadingIndex);
      setCurrentLoadingIndex((prev) => prev + 1);
    }
  }, [
    thread,
    currentIndex,
    currentLoadingIndex,
    loadOnlySelectedMessage,
    inboxEmail,
  ]);

  const retryFetchMessage = async (messageIndex: number) => {
    if (!thread || !thread.messages || messageIndex >= thread.messages.length) {
      return;
    }

    try {
      // Set loading state
      setThread((prev) => {
        if (!prev) return null;
        const updatedMessages = [...prev.messages];
        if (updatedMessages[messageIndex]) {
          (updatedMessages[messageIndex] as any)._loading = true;
          (updatedMessages[messageIndex] as any)._error = false;
          // Clear the error message
          updatedMessages[messageIndex].body = undefined;
          updatedMessages[messageIndex].htmlBody = undefined;
        }
        return { ...prev, messages: updatedMessages };
      });

      const message = thread.messages[messageIndex];
      const url = `/documents/email/thread/${message.threadId}/message/${message.messageId}?extracted_data=1&inboxEmail=${encodeURIComponent(inboxEmail || "")}&sharedInbox=${encodeURIComponent(sharedInbox || "")}`;
      const response = await apiClient.get<EmailMessage>(url, {
        withCredentials: true,
      });
      const details = response;

      setThread((prev) => {
        if (!prev) return null;
        const updatedMessages = [...prev.messages];
        updatedMessages[messageIndex] = {
          ...updatedMessages[messageIndex],
          body: details.body,
          htmlBody: details.htmlBody,
          attachments: details.attachments || [],
          mailAttachments: details.mailAttachments || [],
          tags: details.tags,
          metadata: details.metadata,
          extractedData: details.extractedData,
        };
        (updatedMessages[messageIndex] as any)._loading = false;
        return { ...prev, messages: updatedMessages };
      });
    } catch (err) {
      setThread((prev) => {
        if (!prev) return null;
        const updatedMessages = [...prev.messages];
        updatedMessages[messageIndex] = {
          ...updatedMessages[messageIndex],
          body: "Error Fetching Email 🥺 \n",
          attachments: [],
          mailAttachments: [],
        };
        (updatedMessages[messageIndex] as any)._loading = false;
        return { ...prev, messages: updatedMessages };
      });
      console.error("Retry fetch failed:", err);
    }
  };

  if (initialThreadLoading)
    // loading thread spinner
    return (
      <Box
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  if (error)
    return (
      <Typography variant="body1" color="error">
        Error: {error}
      </Typography>
    );
  if (!thread) return <Typography variant="body1">Thread not found</Typography>;

  const messages = thread.messages || [];
  const totalMessages = messages.length;
  const currentMessage = messages[currentIndex];

  // If no messages in thread, show a message (but only if we're not still loading)
  if (totalMessages === 0 && !initialThreadLoading) {
    return (
      <Typography variant="body1" color="textSecondary">
        No messages found in this thread
      </Typography>
    );
  }

  return (
    <Box
      display="flex"
      maxWidth="md"
      sx={[{ overflowY: "auto", height: "100%" }, sx as any]}
      p={2}
    >
      <Box sx={{ flexGrow: 1, maxWidth: "100%" }}>
        {/* Show inbox email chip when viewing as admin */}
        {inboxEmail && (
          <Box mb={2}>
            <Chip
              label={`Viewing ${inboxEmail}'s inbox`}
              color="primary"
              size="small"
              sx={{
                backgroundColor: "rgba(25, 118, 210, 0.08)",
                borderColor: "rgba(25, 118, 210, 0.23)",
                color: "rgba(25, 118, 210, 0.87)",
                fontWeight: "bold",
              }}
            />
          </Box>
        )}

        <Typography variant="h5" fontSize={18} fontWeight="bold" mb={1} mt={0}>
          {thread.thread?.subject ||
            thread.messages[0]?.subject ||
            "No Subject"}
        </Typography>

        {/* Navigation & Counter */}
        <Box display="flex" alignItems="center" mb={2}>
          {/* Up/Down Buttons */}
          <IconButton
            color="primary"
            onClick={() => setCurrentIndex((prev) => prev - 1)}
            disabled={currentIndex === 0}
            sx={{ mr: 1 }}
          >
            <ArrowUpwardIcon />
          </IconButton>
          <IconButton
            color="primary"
            onClick={() => setCurrentIndex((prev) => prev + 1)}
            disabled={currentIndex === totalMessages - 1}
          >
            <ArrowDownwardIcon />
          </IconButton>
          {/* Message Counter */}
          <Typography variant="caption" fontWeight="bold" sx={{ ml: 2 }}>
            Showing message {currentIndex + 1} / {totalMessages}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <DownloadFileButton
            name="Download Email"
            url={`${getServiceUrl()}/documents/email/download_eml/${currentMessage?.messageId}${inboxEmail ? `?inboxEmail=${encodeURIComponent(inboxEmail)}` : ""}`}
          />
        </Box>

        {/* Admin Panel (Admin Only) - Show before message */}
        {user?.isAdmin && currentMessage && (
          <AdminEmailPanel
            messageId={currentMessage.messageId}
            inboxEmail={inboxEmail}
            sharedInbox={sharedInbox}
          />
        )}

        {/* Email Message */}
        <EmailMessageComponent
          message={currentMessage}
          onSelectForwardedEmail={setSelectedForwardedEmail}
          onRetryMessage={() => retryFetchMessage(currentIndex)}
          showMessageIds={showMessageIds}
        />

        {/* Retry Button for Failed Messages */}
        {currentMessage && (currentMessage as any)._error && (
          <Box display="flex" justifyContent="center" mt={2}>
            <IconButton
              color="primary"
              onClick={() => retryFetchMessage(currentIndex)}
              disabled={(currentMessage as any)._loading}
              sx={{
                backgroundColor: "rgba(25, 118, 210, 0.04)",
                border: "1px solid rgba(25, 118, 210, 0.5)",
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Floating Metadata Box */}
      {showMessageMetadata && currentMessage && (
        <Paper
          sx={{
            position: "absolute",
            right: 20,
            width: "370px",
            height: "500px",
            overflow: "auto",
            backgroundColor: "#f9f9f9",
            borderRadius: 2,
            padding: 2,
            boxShadow: 1,
          }}
        >
          {/* Tags */}
          {currentMessage.tags && (
            <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
              {currentMessage.tags.map((t: string) => (
                <TinyValueComponent key={t} value={t} autocolor />
              ))}
            </Box>
          )}

          {/* info */}
          {
            <>
              <TinyValueComponent
                label={"id:"}
                value={currentMessage.messageId}
                autocolor
              />
              <TinyValueComponent
                label={"thread:"}
                value={currentMessage.threadId!}
                autocolor
              />
            </>
          }
          {/* Metadata */}
          {Array.isArray(currentMessage.metadata)
            ? currentMessage.metadata.map((item: any, idx: number) => (
                <Box key={idx} mb={2}>
                  <JSONString
                    topLevelFontSize={10}
                    nestedFontSize={8}
                    data={item}
                  />
                </Box>
              ))
            : currentMessage.metadata && (
                <JSONString
                  topLevelFontSize={10}
                  nestedFontSize={8}
                  data={currentMessage.metadata}
                />
              )}

          {/* Extracted Data*/}
          {Array.isArray(currentMessage.extractedData)
            ? currentMessage.extractedData.map((item: any, idx: number) => (
                <Box key={idx} mb={2}>
                  <JSONString
                    topLevelFontSize={10}
                    nestedFontSize={8}
                    data={item}
                  />
                </Box>
              ))
            : currentMessage.extractedData && (
                <JSONString
                  topLevelFontSize={10}
                  nestedFontSize={8}
                  data={currentMessage.extractedData}
                />
              )}
        </Paper>
      )}

      {/* Forwarded Email Modal */}
      {selectedForwardedEmail && (
        <Dialog
          open={Boolean(selectedForwardedEmail)}
          onClose={() => setSelectedForwardedEmail(null)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            {selectedForwardedEmail.subject}
            <IconButton
              aria-label="close"
              onClick={() => setSelectedForwardedEmail(null)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <EmailMessageComponent
              message={selectedForwardedEmail}
              onSelectForwardedEmail={setSelectedForwardedEmail}
            />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};
